import Authentication from "./server/Authentication.js";
import RegistrarUser from "./server/RegistrarUser.js";
import Administration from "./server/Administration.js";
import SuperAdministration from "./server/SuperAdministration.js";
import Network from "./server/Network.js";
import Subscribe from "./server/Subscribe.js";

import Error from "./utils/Error.js";

import global from "./global.js";

import {LOCAL_SPACE_NUMBER} from "./storage/constants.js";

const log = global.log.getLogger("f_mtac.Server");
log.setLevel(log.levels.WARN);

let Server = function () {
    var authentication = new Authentication(this);
    var registrar = new RegistrarUser(this);
    var subscribe = new Subscribe(this);
    var administration = new Administration(this);
    var superadministration = new SuperAdministration(this);
    var network = new Network(this);
    var uploadingCancelled = false; // AGPOc-9068 : global varaible to know if media iuploading opeariotn has been cancelled

    Object.defineProperty(this, "configuration", {
        set: () => {},
        get: () => global.configuration.server
    });

    Object.defineProperty(this, "authentication", {
        set: () => {},
        get: () => authentication
    });

    Object.defineProperty(this, "registrar", {
        set: () => {},
        get: () => registrar
    });

    Object.defineProperty(this, "subscribe", {
        set: () => {},
        get: () => subscribe
    });

    Object.defineProperty(this, "network", {
        set: () => {},
        get: () => network
    });

    Object.defineProperty(this, "administration", {
        set: () => {},
        get: () => administration
    });

    Object.defineProperty(this, "superadministration", {
        set: () => {},
        get: () => superadministration
    });

    Object.defineProperty(this, "uploadingCancelled", {
        set: (value) => {
            uploadingCancelled = value;
        },
        get: () => uploadingCancelled
    });

    const full_url = (path) => {
        if (path.startsWith("/")) {
            return new URL(`${global.configuration.server.address}${path}`);
        } else {
            return new URL(path);
        }
    };

    const server_common_fetch_form = async (path, method, data, multipart, json, error_handlers, options) => {
        const request = network.fetch_request(full_url(path), method, data, multipart, json, options);
        return network.fetch(request.url, request.options).then(async (response) => {
            if (response.ok) {
                return response;
            }
            var error = await network.common_fetch_error(response);
            log.debug("Server.server_common_fetch_form status =", {error});
            if (error_handlers !== undefined && error.status in error_handlers) {
                return error_handlers[error.status](error, request);
            }
            return network.common_fetch_error_handler(error);
        });
    };

    this.post = (path, data, error_handlers, options) => {
        log.debug(`Server.post ${path}`, data);
        return server_common_fetch_form(path, "POST", data, false, false, error_handlers, options);
    };
    this.post_json = (path, data, error_handlers, options) => {
        log.debug(`Server.post ${path}`, data);
        return server_common_fetch_form(path, "POST", data, false, true, error_handlers, options);
    };
    this.put = (path, data, error_handlers, options) => {
        log.debug(`Server.put ${path}`, data);
        return server_common_fetch_form(path, "PUT", data, false, false, error_handlers, options);
    };
    this.get = (path, data, error_handlers, options) => {
        log.debug(`Server.get ${path}`, data);
        return server_common_fetch_form(path, "GET", data, false, false, error_handlers, options);
    };
    this.delete = (path, data, error_handlers, options) => {
        log.debug(`Server.delete ${path}`, data);
        return server_common_fetch_form(path, "DELETE", data, false, false, error_handlers, options);
    };
    this.upload = (path, data, error_handlers, options) => {
        log.debug(`Server.upload ${path}`, data);
        return server_common_fetch_form(path, "POST", data, true, false, error_handlers, options);
    };

    const response_to_json = (response) => response.json();
    const response_to_blob = (response) => response.blob();
    const response_to_text = (response) => response.text();

    /**
     * @brief Handle the behavior to have when a response is unauth
     * @param {*} error
     * @param {*} param1
     * @returns
     */
    const handle_unauthaurized = async (error, {url, options}) => {
        log.debug(`Server.handle_unauthaurized error = ${error}`);
        /* auto-login for Agnet too, to avoid filling credentials
        if (global.configuration.user.agnet !== undefined) {
            log.debug("Server:auto_login AGNET mode, do nothing !");
            return;
        }*/
        if (error.code === Error.codes.EACCES) {
            await authentication.auto_login();
        } else {
            throw new Error.build.auth({
                message: "not authorized",
                data: {error}
            });
        }
        return network.fetch(url, options);
    };

    this.init = async () => {
        log.debug("Server.init in");
        var address = `${window.location.origin}/mtac`;
        log.warn("Server.init initialized with address", address);
        window.document.location = "#";
        await global.configuration.add_store("server", {address, profile: null});
        await registrar.init();
    };

    this.mtac_ping = () => {
        log.debug("Server.mtac_ping in ");
        return this.post(
            "/ping",
            {
                tx_time: Date.now() * 1e3
            },
            {
                401: handle_unauthaurized
            },
            {
                retry: false,
                timeout: 2500
            }
        ).then(response_to_json);
    };

    this.mtac_license_retrieve = () => {
        log.debug("Server.mtac_license_retrieve in");
        var {organization} = global.commissioning;
        return this.get(
            `/static/commissioning/${organization}/license.html`,
            {},
            {
                404: (error) => {
                    throw new Error.build.noent({
                        message: `no license for this organization (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_text);
    };

    this.mtac_organization_list = () => {
        log.debug("Server.mtac_organization_list in");
        return this.get("/commissioning/organization").then(response_to_json);
    };

    // Get the list of languages/
    // This is the list of folders under locales/
    this.mtac_language_list = () => {
        log.debug("Server.mtac_language_list in");
        return this.get("/locales/list").then(response_to_json);
    };

    // room

    this.mtac_room_create = ({room_id, room_name, org_name, transaction}) => {
        log.debug("Server.mtac_room_create in");
        return this.put(
            "/room",
            {room_id, room_name, org_name, transaction},
            {
                401: handle_unauthaurized,
                409: (error) => {
                    log.debug("Server.mtac_room_create received 409");
                    throw new Error.build.conflict({
                        message: "room already exist",
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    // @brief call PUT room with transtaction = end
    this.mtac_room_creation_end = ({room_id, room_name, org_name}) => {
        log.debug("Server.mtac_room_creation_end in");
        return this.put(
            "/room",
            {room_id, room_name, org_name, transaction: "end"},
            {
                401: handle_unauthaurized
            }
        ).then(response_to_json);
    };

    this.mtac_room_connect = ({space, desktop, secret, org_name, room_name}) => {
        log.debug("Server.mtac_room_connect in");
        return this.get(
            "/room",
            {
                space,
                desktop,
                secret,
                org_name,
                room_name
            },
            {
                401: handle_unauthaurized,
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the room was not found on the server (${error.message})`,
                        data: {error}
                    });
                },
                409: (error) => {
                    log.debug("Server.mtac_room_connect received 409");
                    throw new Error.build.conflict({
                        message: "room already exist",
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    this.mtac_room_update = ({space, name}) => {
        log.debug("Server.mtac_room_update in");
        return this.post(
            "/room",
            {space, name},
            {
                401: handle_unauthaurized,
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the room was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    this.mtac_room_desktop_list = ({space}) => {
        log.debug(`Server.mtac_room_desktop_list in space = ${space}`);
        return this.get(
            "/room",
            {space},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized",
                        data: {error}
                    });
                },
                404: (error) => {
                    log.debug("Server.mtac_room_desktop_list received 404");
                    throw new Error.build.noent({
                        message: `the room was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    this.mtac_room_check = ({space}) => {
        log.debug("Server.mtac_room_check in");
        return this.get(
            "/room",
            {space},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    log.debug("Server.mtac_room_check received 404");
                    throw new Error.build.noent({
                        message: `the room was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then((response_to_json) => {
            log.debug("Server.mtac_room_check response", {response_to_json});
            return response_to_json;
        });
    };

    this.mtac_room_desktop_delete = ({space, desktop, global_hash}) => {
        log.debug("Server.mtac_room_desktop_delete in");
        return this.delete(
            "/room",
            {space, desktop, global_hash},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                409: (error) => {
                    throw new Error.build.conflict({
                        message: "room delete conflict",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the room was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };
    // TO DO : add org_name
    this.mtac_room_disconnect = ({space}) => {
        log.debug("Server.mtac_room_disconnect in");
        return this.delete(
            "/room",
            {space},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the room was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };
    this.mtac_room_list = ({space, name}) => this.post("/room", {space, name}).then(response_to_json);
    this.mtac_room_users = ({space}) => {
        log.debug("Server.mtac_room_users in");
        return this.get(
            "/room/users",
            {space},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the space was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    // object
    this.mtac_object_retrieve = ({space, desktop, global_hash}) => {
        log.debug("Server.mtac_object_retrieve in");
        if (space === undefined || desktop === undefined) {
            throw new Error.build.invalid("space and desktop must be defined");
        }
        return this.get(
            "/object",
            {space, desktop, global_hash},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the room or object was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    this.mtac_object_update = ({space, desktop, old_hash, new_hash, object_data, global_hash, msisdn, user}) => {
        log.debug("Server.mtac_object_update in");
        if (space === undefined || desktop === undefined) {
            throw new Error.build.invalid("space and desktop must be defined");
        }
        if (old_hash === undefined && new_hash === undefined) {
            throw new Error.build.invalid("either new or old hash must be defined");
        }
        return this.put(
            "/object",
            {space, desktop, old_hash, new_hash, object: object_data, global_hash, msisdn, user},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the room was not found on the server (${error.message})`,
                        data: {error}
                    });
                },
                409: (error) => {
                    throw new Error.build.conflict({
                        message: `unable to put resource because of a conflict (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    this.mtac_object_delete = ({space, desktop}) => {
        log.debug("Server.mtac_object_delete in");
        if (space === undefined || desktop === undefined) {
            throw new Error.build.invalid("space and desktop must be defined");
        }
        return this.delete(
            "/object",
            {space, desktop},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the room was not found on the server (${error.message})`,
                        data: {error}
                    });
                },
                409: (error) => {
                    throw new Error.build.conflict({
                        message: `unable to put resource because of a conflict (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    // configuration

    this.mtac_configuration_list = ({space, desktop}) => {
        log.debug("Server.mtac_configuration_list in");
        if (space === undefined || desktop === undefined) {
            throw new Error.build.invalid("space and desktop must be defined");
        }
        return this.get(
            "/configuration",
            {space, desktop},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };
    this.mtac_configuration_retrieve = ({space, desktop, configuration_name}) => {
        log.debug("Server.mtac_configuration_retrieve in");
        if (space === undefined || desktop === undefined || configuration_name === undefined) {
            throw new Error.build.invalid("space, desktop and configuration_name must be defined");
        }
        return this.get(
            "/configuration",
            {space, desktop, configuration_name},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the configuration was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };
    this.mtac_configuration_update = ({space, desktop, old_hash, new_hash, configuration_name, configuration_data}) => {
        log.debug("Server.mtac_configuration_update in");
        if (space === undefined || desktop === undefined || configuration_name === undefined) {
            throw new Error.build.invalid("space, desktop and configuration_name must be defined");
        }
        if (old_hash === undefined && new_hash === undefined) {
            throw new Error.build.invalid("either new or old hash must be defined");
        }
        return this.put(
            "/configuration",
            {
                space,
                desktop,
                old_hash,
                new_hash,
                configuration_name,
                configuration_data
            },
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the configuration was not found on the server (${error.message})`,
                        data: {error}
                    });
                },
                409: (error) => {
                    throw new Error.build.conflict({
                        message: `unable to put configuration because of a conflict (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };
    this.mtac_configuration_delete = ({space, desktop, configuration_name}) => {
        log.debug("Server.mtac_configuration_delete in");
        if (space === undefined || desktop === undefined || configuration_name === undefined) {
            throw new Error.build.invalid("space, desktop and configuration_name must be defined");
        }
        return this.delete(
            "/configuration",
            {space, desktop, configuration_name},
            {
                401: handle_unauthaurized,
                400: (error) => {
                    throw new Error.build.invalid({
                        message: `server can't process request for some reason (${error.message})`,
                        data: {error}
                    });
                },
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the configuration was not found on the server (${error.message})`,
                        data: {error}
                    });
                },
                409: (error) => {
                    throw new Error.build.conflict({
                        message: `unable to put configuration because of a conflict (${error.message})`,
                        data: {error}
                    });
                },
                500: (error) => {
                    throw new Error.build.fatal({
                        message: `internal server failure (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    // media
    this.mtac_media_list = ({space}) => {
        log.debug("Server.mtac_media_list in");
        if (space === undefined) {
            throw new Error.build.invalid("space must be defined");
        }
        return this.get(
            "/media",
            {space},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the space was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    //AGPOC-9068 : implements cancel uploading media operation
    let controller = null;
    this.abort = () => {
        log.debug("Server.abort in");
        if (controller != null) {
            // fetch POST media has been sent
            controller.abort();
            controller = null;
        }
    };
    this.mtac_media_create = ({space, media_hash, media_data}) => {
        log.debug("Server.mtac_media_create in");
        controller = new AbortController(); // creates AbortController
        return this.upload(
            "/media",
            {
                space,
                media_hash,
                media_data
            },
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the space was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            },
            {
                signal: controller.signal // AGPOC-9068 : put AbortController signal in fetch request
            }
        ).then((response) => {
            response.json();
            controller = null; // reinit AbortController
        });
    };
    this.mtac_media_retrieve = ({space, hash}) => {
        log.debug("Server.mtac_media_retrieve in");
        if (space === undefined || hash === undefined) {
            throw new Error.build.invalid("space and hash must be defined");
        }
        return this.get(
            `/media/storage/${space}/${hash}`,
            {},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the media was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_blob);
    };

    // TODO: Move this method to another module as this one is dedicated to Tacteam server requests.
    // Note that making CORS (Cross Origin) request in the browser is delicate and should probably be done on the server.
    this.mtac_static_media_retrieve = ({url, data}) => {
        log.debug("Server.mtac_static_media_retrieve in");
        return this.get(url, data, {
            401: handle_unauthaurized,
            403: (error) => {
                throw new Error.build.auth({
                    message: "not authorized or room doesn't exist",
                    data: {error}
                });
            },
            404: (error) => {
                throw new Error.build.noent({
                    message: `the media was not found on the server (${error.message})`,
                    data: {error}
                });
            }
        }).then(response_to_blob);
    };

    this.mtac_media_delete = ({space, media_hash}) => {
        log.debug("Server.mtac_media_delete in");
        if (space === undefined || media_hash === undefined) {
            throw new Error.build.invalid("space and hash must be defined");
        }
        return this.delete(
            "/media",
            {space, media_hash},
            {
                401: handle_unauthaurized,
                400: (error) => {
                    throw new Error.build.invalid({
                        message: `server can't process request for some reason (${error.message})`,
                        data: {error}
                    });
                },
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the media was not found on the server (${error.message})`,
                        data: {error}
                    });
                },
                500: (error) => {
                    throw new Error.build.fatal({
                        message: `internal server failure (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    // message
    this.mtac_message_create = ({space, text, media_id, type, tx_delta, sender_tx_time}) => {
        log.debug("Server.mtac_message_create in");
        if (space === undefined || text === undefined || type === undefined || sender_tx_time === undefined) {
            throw new Error.build.invalid("space, type, tx_delta, sender_tx_time and text must be defined");
        }
        return this.put(
            "/message",
            {space, text, media_id, type, tx_delta, sender_tx_time},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `unable to post message on server (${error.message})`,
                        data: {error}
                    });
                },
                409: (error) => {
                    // AGPOC-3475 : error 409 used to manage error message already exists (not real conflict)
                    throw new Error.build.exist({
                        message: "message already exists in server",
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };
    this.mtac_message_retrieve = ({space}) => {
        log.debug("Server.mtac_message_retrieve in");
        if (space === undefined) {
            throw new Error.build.invalid("space must be defined");
        }
        return this.get(
            "/message",
            {space},
            {
                401: handle_unauthaurized,
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                404: (error) => {
                    throw new Error.build.noent({
                        message: `the space was not found on the server (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(async (response) => {
            let response_json = await response.json();
            if (response_json.data.messages === undefined) {
                log.error("bad format (undefined)");
                return [];
            }
            return response_json.data.messages;
        });
    };
    this.mtac_message_delete = ({space, time_min, time_max}) => {
        log.debug("Server.mtac_message_delete in");
        if (space === undefined) {
            throw new Error.build.invalid("space must be defined");
        }
        return this.delete(
            "/message",
            {space, rx_time_min: time_min, rx_time_max: time_max},
            {
                401: handle_unauthaurized,
                400: (error) => {
                    throw new Error.build.invalid({
                        message: `server can't process request for some reason (${error.message})`,
                        data: {error}
                    });
                },
                403: (error) => {
                    throw new Error.build.auth({
                        message: "not authorized or room doesn't exist",
                        data: {error}
                    });
                },
                409: (error) => {
                    throw new Error.build.conflict({
                        message: `unable to delete messages because of a conflict (${error.message})`,
                        data: {error}
                    });
                },
                500: (error) => {
                    throw new Error.build.fatal({
                        message: `internal server failure (${error.message})`,
                        data: {error}
                    });
                }
            }
        ).then(response_to_json);
    };

    /**
     * @brief Send a logout request to the server
     */
    this.mtac_logout = async () => {
        log.debug("Server.mtac_logout in");
        await authentication.logout();

        // remove indexdb storage. If not if org changed => old org commissioning and old user sitacs kept!
        await global.configuration.remove_store("commissioning");
        await global.configuration.remove_store("user");
        for (let space_index = 0; space_index < LOCAL_SPACE_NUMBER; space_index++) {
            await global.storage.delete_space({space_index});
        }
        // recall Tacteam to display the login page
        window.location.replace("/mtac");
    };
};

global.app.bootstrap.register_module({
    name: "server",
    deps: ["app", "configuration"],
    init: async () => {
        global.server = new Server();
        await global.server.init();
    }
});

export default Server;
